import { useState, useEffect, useCallback } from "react";
import { Plus, Pencil, Trash2, History, GitCompareArrows, Search, X } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { useLang } from "../lib/i18n";
import { fmtIDR, fmtNum, fmtDate } from "../lib/format";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

export default function Recipes() {
  const { t } = useLang();
  const [recipes, setRecipes] = useState([]);
  const [meta, setMeta] = useState({ categories: [] });
  const [ingredients, setIngredients] = useState([]);
  const [bases, setBases] = useState([]);
  const [products, setProducts] = useState([]);
  const [settings, setSettings] = useState({ target_food_cost: 0.4, rounding: 500 });
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(null);
  const [historyFor, setHistoryFor] = useState(null);
  const [history, setHistory] = useState([]);
  const [compareSel, setCompareSel] = useState({ a: "", b: "" });
  const [compareResult, setCompareResult] = useState(null);

  const load = useCallback(async () => {
    try {
      const [r, m, i, p, s, b] = await Promise.all([
        api.get("/recipes", { params: q ? { q } : {} }),
        api.get("/meta/all"),
        api.get("/ingredients"),
        api.get("/products"),
        api.get("/settings"),
        api.get("/bases"),
      ]);
      setRecipes(r.data);
      setMeta(m.data);
      setIngredients(i.data);
      setProducts(p.data);
      setSettings(s.data);
      setBases(b.data);
    } catch (e) { toast.error(errMsg(e)); }
  }, [q]);

  useEffect(() => { load(); }, [load]);

  const emptyForm = () => ({ name: "", category_id: "", product_id: "", image: "", description: "", yield_qty: 1, serving_size: "", wastage_pct: 0, ingredients: [], note: "" });

  const openForm = (r) => {
    setEditing(r || null);
    setForm(r ? {
      name: r.name, category_id: r.category_id || "", product_id: r.product_id || "", image: r.image || "",
      description: r.description || "", yield_qty: r.yield_qty, serving_size: r.serving_size || "",
      wastage_pct: r.wastage_pct || 0, ingredients: r.ingredients.map((i) => ({ ingredient_id: i.ingredient_id || "", base_id: i.base_id || "", qty: i.qty })), note: "",
    } : emptyForm());
    setOpen(true);
  };

  const ingInfo = (id) => ingredients.find((i) => i.id === id) || {};
  const ingPrice = (id) => { const i = ingInfo(id); return (i.avg_cost > 0 ? i.avg_cost : i.price) || 0; };
  const basePrice = (id) => bases.find((b) => b.id === id)?.cost_per_unit || 0;
  const rowPrice = (it) => (it.base_id ? basePrice(it.base_id) : ingPrice(it.ingredient_id));
  const totals = () => {
    if (!form) return { total: 0, hpp: 0, suggested: 0 };
    const total = form.ingredients.reduce((s, it) => s + it.qty * rowPrice(it), 0) * (1 + (form.wastage_pct || 0) / 100);
    const y = form.yield_qty > 0 ? form.yield_qty : 1;
    const hpp = total / y;
    const tfc = settings.target_food_cost || 0.4;
    const raw = tfc > 0 ? hpp / tfc : hpp;
    const rounding = settings.rounding || 0;
    const suggested = rounding > 0 ? Math.round(raw / rounding) * rounding : Math.round(raw);
    return { total, hpp, suggested };
  };

  const save = async () => {
    try {
      if (editing) await api.put(`/recipes/${editing.id}`, form);
      else await api.post("/recipes", form);
      toast.success(t("save") + " OK");
      setOpen(false);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const remove = async (r) => {
    if (!window.confirm(t("confirmDelete"))) return;
    try { await api.delete(`/recipes/${r.id}`); toast.success("OK"); load(); } catch (e) { toast.error(errMsg(e)); }
  };

  const openHistory = async (r) => {
    setHistoryFor(r);
    setCompareResult(null);
    setCompareSel({ a: "", b: "" });
    try {
      const res = await api.get(`/recipes/${r.id}/history`);
      setHistory(res.data);
    } catch { setHistory([]); }
  };

  const runCompare = async () => {
    if (!compareSel.a || !compareSel.b || compareSel.a === compareSel.b) return;
    try {
      const res = await api.get(`/recipes/${historyFor.id}/compare`, { params: { version_a: compareSel.a, version_b: compareSel.b } });
      setCompareResult(res.data);
    } catch (e) { toast.error(errMsg(e)); }
  };

  const tv = totals();

  return (
    <div data-testid="recipes-page">
      <div className="flex flex-wrap items-center gap-3 mb-5">
        <h1 className="text-2xl md:text-3xl font-bold font-heading tracking-tight flex-1">{t("nav_recipes")}</h1>
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input data-testid="recipes-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("search")} className="pl-9 w-52" />
        </div>
        <Button data-testid="recipe-add-btn" onClick={() => openForm(null)} className="bg-amber-600 hover:bg-amber-700 text-white"><Plus className="w-4 h-4 mr-1.5" /> {t("add")}</Button>
      </div>

      <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4">
        {recipes.map((r) => (
          <div key={r.id} data-testid={`recipe-card-${r.id}`} className="bg-card border border-border rounded-xl overflow-hidden shadow-xs hover:shadow-md transition-shadow">
            {r.image && <img src={r.image} alt={r.name} className="h-36 w-full object-cover" />}
            <div className="p-4">
              <div className="flex items-start justify-between gap-2 mb-1">
                <h3 className="font-heading font-semibold">{r.name}</h3>
                <span className="text-[10px] font-mono bg-amber-50 dark:bg-amber-950/50 text-amber-700 dark:text-amber-400 border border-amber-200 dark:border-amber-800 px-2 py-0.5 rounded-full shrink-0">v{r.version}</span>
              </div>
              <div className="text-xs text-muted-foreground mb-3">{r.category_name || "-"} · {r.product_name || "-"} · Yield {fmtNum(r.yield_qty)}</div>
              <div className="grid grid-cols-3 gap-2 text-center mb-3">
                <div className="bg-muted/50 rounded-lg py-2"><div className="text-[10px] uppercase tracking-wider text-muted-foreground">{t("recipeCost")}</div><div className="text-xs font-mono font-bold">{fmtIDR(r.total_cost)}</div></div>
                <div className="bg-muted/50 rounded-lg py-2"><div className="text-[10px] uppercase tracking-wider text-muted-foreground">{t("hppServing")}</div><div className="text-xs font-mono font-bold">{fmtIDR(r.hpp_per_serving)}</div></div>
                <div className="bg-amber-50 dark:bg-amber-950/40 rounded-lg py-2"><div className="text-[10px] uppercase tracking-wider text-amber-700 dark:text-amber-400">{t("suggestedPrice")}</div><div className="text-xs font-mono font-bold text-amber-700 dark:text-amber-400">{fmtIDR(r.suggested_price)}</div></div>
              </div>
              <div className="flex gap-1.5">
                <Button size="sm" variant="outline" data-testid={`recipe-edit-${r.id}`} onClick={() => openForm(r)} className="flex-1"><Pencil className="w-3.5 h-3.5 mr-1" />{t("edit")}</Button>
                <Button size="sm" variant="outline" data-testid={`recipe-history-${r.id}`} onClick={() => openHistory(r)}><History className="w-3.5 h-3.5" /></Button>
                <Button size="sm" variant="outline" data-testid={`recipe-delete-${r.id}`} onClick={() => remove(r)} className="text-rose-600 hover:text-rose-700"><Trash2 className="w-3.5 h-3.5" /></Button>
              </div>
            </div>
          </div>
        ))}
        {recipes.length === 0 && <div className="col-span-full text-center text-muted-foreground py-16">-</div>}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="recipe-form-dialog">
          <DialogHeader><DialogTitle className="font-heading">{editing ? t("edit") : t("add")} Recipe</DialogTitle></DialogHeader>
          {form && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2"><Label className="text-xs uppercase tracking-wider text-muted-foreground">{t("name")}</Label>
                  <Input data-testid="recipe-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5" /></div>
                <div><Label className="text-xs uppercase tracking-wider text-muted-foreground">{t("category")}</Label>
                  <Select value={form.category_id} onValueChange={(v) => setForm({ ...form, category_id: v })}>
                    <SelectTrigger data-testid="recipe-category-select" className="mt-1.5"><SelectValue /></SelectTrigger>
                    <SelectContent>{meta.categories.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                  </Select></div>
                <div><Label className="text-xs uppercase tracking-wider text-muted-foreground">{t("product")}</Label>
                  <Select value={form.product_id || "none"} onValueChange={(v) => setForm({ ...form, product_id: v === "none" ? "" : v })}>
                    <SelectTrigger data-testid="recipe-product-select" className="mt-1.5"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="none">-</SelectItem>{products.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
                  </Select>
                  <p className="text-[11px] text-muted-foreground mt-1">Kosongkan untuk membuat produk baru otomatis dari resep ini.</p></div>
                <div><Label className="text-xs uppercase tracking-wider text-muted-foreground">{t("yieldQty")}</Label>
                  <Input data-testid="recipe-yield-input" type="number" value={form.yield_qty} onChange={(e) => setForm({ ...form, yield_qty: Number(e.target.value) })} className="mt-1.5" /></div>
                <div><Label className="text-xs uppercase tracking-wider text-muted-foreground">{t("servingSize")}</Label>
                  <Input data-testid="recipe-serving-input" value={form.serving_size} onChange={(e) => setForm({ ...form, serving_size: e.target.value })} className="mt-1.5" /></div>
                <div><Label className="text-xs uppercase tracking-wider text-muted-foreground">{t("wastage")}</Label>
                  <Input data-testid="recipe-wastage-input" type="number" value={form.wastage_pct} onChange={(e) => setForm({ ...form, wastage_pct: Number(e.target.value) })} className="mt-1.5" /></div>
                <div><Label className="text-xs uppercase tracking-wider text-muted-foreground">Foto URL</Label>
                  <Input data-testid="recipe-image-input" value={form.image} onChange={(e) => setForm({ ...form, image: e.target.value })} className="mt-1.5" /></div>
                <div className="col-span-2"><Label className="text-xs uppercase tracking-wider text-muted-foreground">Deskripsi</Label>
                  <Textarea data-testid="recipe-desc-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1.5" /></div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-xs uppercase tracking-wider text-muted-foreground">{t("ingredients")}</Label>
                  <Button size="sm" variant="outline" data-testid="recipe-add-ingredient-btn" onClick={() => setForm({ ...form, ingredients: [...form.ingredients, { ingredient_id: "", base_id: "", qty: 1 }] })}><Plus className="w-3.5 h-3.5 mr-1" />{t("addIngredient")}</Button>
                </div>
                <div className="space-y-2">
                  {form.ingredients.map((it, idx) => (
                    <div key={it.base_id ? `b:${it.base_id}` : it.ingredient_id ? `i:${it.ingredient_id}` : `empty-${idx}`} className="flex items-center gap-2" data-testid={`recipe-ingredient-row-${idx}`}>
                      <Select value={it.base_id ? `b:${it.base_id}` : it.ingredient_id ? `i:${it.ingredient_id}` : ""} onValueChange={(v) => { const [kind, id] = v.split(":"); const arr = [...form.ingredients]; arr[idx] = kind === "b" ? { base_id: id, ingredient_id: "", qty: arr[idx].qty } : { ingredient_id: id, base_id: "", qty: arr[idx].qty }; setForm({ ...form, ingredients: arr }); }}>
                        <SelectTrigger data-testid={`recipe-ing-select-${idx}`} className="flex-1"><SelectValue placeholder="..." /></SelectTrigger>
                        <SelectContent>
                          {ingredients.map((i) => <SelectItem key={i.id} value={`i:${i.id}`}>{i.name}</SelectItem>)}
                          {bases.map((b) => <SelectItem key={b.id} value={`b:${b.id}`}>▸ {b.name} (Base)</SelectItem>)}
                        </SelectContent>
                      </Select>
                      <Input data-testid={`recipe-ing-qty-${idx}`} type="number" value={it.qty} onChange={(e) => { const arr = [...form.ingredients]; arr[idx] = { ...arr[idx], qty: Number(e.target.value) }; setForm({ ...form, ingredients: arr }); }} className="w-24" />
                      <div className="w-24 text-xs font-mono text-muted-foreground">{fmtIDR(it.qty * rowPrice(it))}</div>
                      <button onClick={() => setForm({ ...form, ingredients: form.ingredients.filter((_, i) => i !== idx) })} className="p-1.5 text-muted-foreground hover:text-rose-600"><X className="w-4 h-4" /></button>
                    </div>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-3 gap-2 text-center bg-muted/40 rounded-xl p-3">
                <div><div className="text-[10px] uppercase tracking-wider text-muted-foreground">{t("recipeCost")}</div><div className="font-mono font-bold" data-testid="recipe-total-cost">{fmtIDR(tv.total)}</div></div>
                <div><div className="text-[10px] uppercase tracking-wider text-muted-foreground">{t("hppServing")}</div><div className="font-mono font-bold" data-testid="recipe-hpp">{fmtIDR(tv.hpp)}</div></div>
                <div><div className="text-[10px] uppercase tracking-wider text-amber-700 dark:text-amber-400">{t("suggestedPrice")}</div><div className="font-mono font-bold text-amber-700 dark:text-amber-400" data-testid="recipe-suggested">{fmtIDR(tv.suggested)}</div></div>
              </div>
              {editing && <div><Label className="text-xs uppercase tracking-wider text-muted-foreground">Note</Label>
                <Input data-testid="recipe-note-input" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} className="mt-1.5" /></div>}
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setOpen(false)} data-testid="recipe-cancel-btn">{t("cancel")}</Button>
                <Button onClick={save} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="recipe-save-btn">{t("save")}</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!historyFor} onOpenChange={() => setHistoryFor(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="recipe-history-dialog">
          <DialogHeader><DialogTitle className="font-heading">{t("history")} — {historyFor?.name}</DialogTitle></DialogHeader>
          <div className="space-y-2 mb-5">
            {history.map((h) => (
              <div key={h.id} data-testid={`history-row-v${h.version}`} className="flex items-center gap-3 bg-muted/40 rounded-lg px-3 py-2 text-sm">
                <span className="font-mono font-bold text-amber-600">v{h.version}</span>
                <span className="text-muted-foreground flex-1 truncate">{h.note || "-"} · {h.changed_by}</span>
                <span className="font-mono text-xs">{fmtIDR(h.snapshot?.total_cost)}</span>
                <span className="text-xs text-muted-foreground">{fmtDate(h.created_at)}</span>
              </div>
            ))}
          </div>
          <div className="border-t border-border pt-4">
            <div className="flex items-end gap-2 mb-3">
              <div><Label className="text-xs uppercase tracking-wider text-muted-foreground">{t("compare")} A</Label>
                <Select value={compareSel.a} onValueChange={(v) => setCompareSel({ ...compareSel, a: v })}>
                  <SelectTrigger data-testid="compare-a-select" className="mt-1 w-28"><SelectValue /></SelectTrigger>
                  <SelectContent>{history.map((h) => <SelectItem key={h.version} value={String(h.version)}>v{h.version}</SelectItem>)}</SelectContent>
                </Select></div>
              <div><Label className="text-xs uppercase tracking-wider text-muted-foreground">B</Label>
                <Select value={compareSel.b} onValueChange={(v) => setCompareSel({ ...compareSel, b: v })}>
                  <SelectTrigger data-testid="compare-b-select" className="mt-1 w-28"><SelectValue /></SelectTrigger>
                  <SelectContent>{history.map((h) => <SelectItem key={h.version} value={String(h.version)}>v{h.version}</SelectItem>)}</SelectContent>
                </Select></div>
              <Button data-testid="compare-run-btn" onClick={runCompare} className="bg-amber-600 hover:bg-amber-700 text-white"><GitCompareArrows className="w-4 h-4 mr-1.5" />{t("compare")}</Button>
            </div>
            {compareResult && (
              <div data-testid="compare-result">
                <div className="grid grid-cols-3 gap-2 text-center mb-3 text-sm">
                  <div className="bg-muted/40 rounded-lg py-2">v{compareResult.version_a.version}<div className="font-mono font-bold">{fmtIDR(compareResult.version_a.total_cost)}</div></div>
                  <div className="bg-muted/40 rounded-lg py-2">v{compareResult.version_b.version}<div className="font-mono font-bold">{fmtIDR(compareResult.version_b.total_cost)}</div></div>
                  <div className="bg-amber-50 dark:bg-amber-950/40 rounded-lg py-2">Δ<div className="font-mono font-bold text-amber-700 dark:text-amber-400">{fmtIDR(compareResult.version_b.total_cost - compareResult.version_a.total_cost)}</div></div>
                </div>
                <table className="w-full text-xs">
                  <thead><tr className="border-b border-border text-muted-foreground uppercase tracking-wider"><th className="text-left py-1.5">{t("name")}</th><th className="text-right">Qty A</th><th className="text-right">Qty B</th><th className="text-right">{t("cost")} A</th><th className="text-right">{t("cost")} B</th><th className="text-right">{t("status")}</th></tr></thead>
                  <tbody>
                    {compareResult.diff.map((d, i) => (
                      <tr key={d.name || i} className="border-b border-border last:border-0">
                        <td className="py-1.5">{d.name}</td><td className="text-right font-mono">{fmtNum(d.qty_a)}</td><td className="text-right font-mono">{fmtNum(d.qty_b)}</td>
                        <td className="text-right font-mono">{fmtIDR(d.cost_a)}</td><td className="text-right font-mono">{fmtIDR(d.cost_b)}</td>
                        <td className="text-right"><span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${d.status === "changed" ? "bg-amber-50 text-amber-700 dark:bg-amber-950/50 dark:text-amber-400" : d.status === "added" ? "bg-emerald-50 text-emerald-700 dark:bg-emerald-950/50 dark:text-emerald-400" : "bg-rose-50 text-rose-700 dark:bg-rose-950/50 dark:text-rose-400"}`}>{d.status}</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
